import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import * as Joi from "joi";
import { parseSafeHtml } from "../../../../utils/safeHtml";
import useAuth from "../../../../hooks/useAuth";
import { getLanguageCode } from "../../../../utils/languageUtility";
import { momentLocaleOf } from "../../../../utils/momentLocale";
import moment from "moment";
import { Checkbox, Label } from "flowbite-react";
import { Link, useParams } from "react-router-dom";
import { CustomButton } from "../../../atoms/CustomButton";
import { ModalShell } from "../../../atoms/ModalShell";
import {
  useGetTrackerOptions,
  useUpdateTracker,
} from "../../../../hooks/users";
import { AxiosResponse } from "axios";
import { HiArrowLeft, HiOutlineDownload } from "react-icons/hi";

const schema = Joi.object({
  events: Joi.array().min(1).required(),
}).required();

const eventList: any = {
  ALL: {
    code: "ALL",
    description: {
      en: "All events",
      fr: "Tous les événements",
    },
  },
  USER_LOGIN_SUCCESS: {
    code: "USER_LOGIN_SUCCESS",
    description: {
      en: "User logged in successfully",
      fr: "Connexion utilisateur réussie",
    },
  },
  USER_LOGIN_FAILED: {
    code: "USER_LOGIN_FAILED",
    description: {
      en: "User logged in failed",
      fr: "Échec de la connexion utilisateur",
    },
  },
  USER_LOGIN_SERVICE_OFF: {
    code: "USER_LOGIN_SERVICE_OFF",
    description: {
      en: "User try to login but service was off",
      fr: "L'utilisateur a essayé de se connecter mais le service était indisponible",
    },
  },
  USER_LOGIN_NOT_ACTIVE: {
    code: "USER_LOGIN_NOT_ACTIVE",
    description: {
      en: "User try to login but the account was not active",
      fr: "L'utilisateur a essayé de se connecter mais le compte n'était pas actif",
    },
  },
  USER_LOGIN_BAD_CREDENTIAL: {
    code: "USER_LOGIN_BAD_CREDENTIAL",
    description: {
      en: "User try to login but with bad credential",
      fr: "L'utilisateur a essayé de se connecter avec des identifiants incorrects",
    },
  },
  USER_CREATED: {
    code: "USER_CREATED",
    description: {
      en: "User created",
      fr: "Utilisateur créé",
    },
  },
  USER_LOGIN_PASSLESS_DEVICE_ERROR: {
    code: "USER_LOGIN_PASSLESS_DEVICE_ERROR",
    description: {
      en: "Passwordless login: answered from a different device than the one that asked",
      fr: "Connexion sans mot de passe : réponse depuis un autre appareil que celui de la demande",
    },
  },
  USER_LOGIN_PASSLESS_DELAY_EXPIRED: {
    code: "USER_LOGIN_PASSLESS_DELAY_EXPIRED",
    description: {
      en: "Passwordless login: request expired",
      fr: "Connexion sans mot de passe : demande expirée",
    },
  },
  USER_LOGIN_PASSLESS_TOKEN_ERROR: {
    code: "USER_LOGIN_PASSLESS_TOKEN_ERROR",
    description: {
      en: "Passwordless login: invalid link",
      fr: "Connexion sans mot de passe : lien invalide",
    },
  },
  USER_LOGIN_PASSLESS_WITHOUT_LINK: {
    code: "USER_LOGIN_PASSLESS_WITHOUT_LINK",
    description: {
      en: "Passwordless login: no request in progress",
      fr: "Connexion sans mot de passe : aucune demande en cours",
    },
  },
  USER_LOGIN_PASSLESS_REQUEST: {
    code: "USER_LOGIN_PASSLESS_REQUEST",
    description: {
      en: "Passwordless login: request sent",
      fr: "Connexion sans mot de passe : demande envoyée",
    },
  },
  USER_LOGIN_PASSLESS_WRONG_SECRET_WORD: {
    code: "USER_LOGIN_PASSLESS_WRONG_SECRET_WORD",
    description: {
      en: "Passwordless login: wrong confirmation word",
      fr: "Connexion sans mot de passe : mot de confirmation incorrect",
    },
  },
  USER_LOGIN_PASSLESS_LINK_EXPIRED: {
    code: "USER_LOGIN_PASSLESS_LINK_EXPIRED",
    description: {
      en: "Passwordless login: time to enter the confirmation word ran out",
      fr: "Connexion sans mot de passe : délai de saisie du mot de confirmation dépassé",
    },
  },
  USER_LOGIN_PASSLESS_LINK_NOT_CONFIRMED: {
    code: "USER_LOGIN_PASSLESS_LINK_NOT_CONFIRMED",
    description: {
      en: "Passwordless login: link not confirmed yet",
      fr: "Connexion sans mot de passe : lien pas encore confirmé",
    },
  },
  USER_LOGIN_PASSLESS_TOO_MANY_ATTEMPTS: {
    code: "USER_LOGIN_PASSLESS_TOO_MANY_ATTEMPTS",
    description: {
      en: "Passwordless login: too many wrong confirmation words, request cancelled",
      fr: "Connexion sans mot de passe : trop de mots de confirmation incorrects, demande annulée",
    },
  },
  USER_LOGIN_PASSKEY_SUCCESS: {
    code: "USER_LOGIN_PASSKEY_SUCCESS",
    description: {
      en: "Passkey: signed in",
      fr: "Passkey : connexion réussie",
    },
  },
  USER_LOGIN_PASSKEY_FAILED: {
    code: "USER_LOGIN_PASSKEY_FAILED",
    description: {
      en: "Passkey: could not be verified",
      fr: "Passkey : vérification impossible",
    },
  },
  USER_LOGIN_PASSKEY_UNKNOWN_CREDENTIAL: {
    code: "USER_LOGIN_PASSKEY_UNKNOWN_CREDENTIAL",
    description: {
      en: "Passkey: not registered for this account",
      fr: "Passkey : non enregistrée pour ce compte",
    },
  },
  USER_LOGIN_PASSKEY_COUNTER_ANOMALY: {
    code: "USER_LOGIN_PASSKEY_COUNTER_ANOMALY",
    description: {
      en: "Passkey: signature counter did not advance, the key may have been copied",
      fr: "Passkey : compteur de signature figé, la clé a peut-être été copiée",
    },
  },
  USER_PASSKEY_REGISTERED: {
    code: "USER_PASSKEY_REGISTERED",
    description: {
      en: "Passkey added",
      fr: "Passkey ajoutée",
    },
  },
  USER_PASSKEY_REGISTRATION_FAILED: {
    code: "USER_PASSKEY_REGISTRATION_FAILED",
    description: {
      en: "Passkey: could not be added",
      fr: "Passkey : ajout impossible",
    },
  },
  USER_PASSKEY_DELETED: {
    code: "USER_PASSKEY_DELETED",
    description: {
      en: "Passkey removed",
      fr: "Passkey supprimée",
    },
  },
  USER_PROFILE_UPDATED: {
    code: "USER_PROFILE_UPDATED",
    description: {
      en: "User update his/her profile",
      fr: "L'utilisateur a mis à jour son profil",
    },
  },
  USER_PASSWORD_UPDATED: {
    code: "USER_PASSWORD_UPDATED",
    description: {
      en: "User update his/her password successfully",
      fr: "L'utilisateur a mis à jour son mot de passe avec succès",
    },
  },
  USER_EMAIL_VALIDATION_SUCCESS: {
    code: "USER_EMAIL_VALIDATION_SUCCESS",
    description: {
      en: "User valid his/her email successfully",
      fr: "L'utilisateur a validé son email avec succès",
    },
  },
  USER_EMAIL_VALIDATION_FAILED: {
    code: "USER_EMAIL_VALIDATION_FAILED",
    description: {
      en: "User failed on valid his/her email",
      fr: "L'utilisateur n'a pas réussi à valider son email",
    },
  },
  USER_EMAIL_VALIDATION_TOKEN_EXPIRED: {
    code: "USER_EMAIL_VALIDATION_TOKEN_EXPIRED",
    description: {
      en: "User validation token email error",
      fr: "Erreur du jeton de validation d'email de l'utilisateur",
    },
  },
  USER_EMAIL_UPDATE_SUCCESS: {
    code: "USER_EMAIL_UPDATE_SUCCESS",
    description: {
      en: "User update his/her email successfully",
      fr: "L'utilisateur a mis à jour son email avec succès",
    },
  },
  USER_EMAIL_UPDATE_ALREADY_USED: {
    code: "USER_EMAIL_UPDATE_ALREADY_USED",
    description: {
      en: "User try to update his/her email by an already existing email",
      fr: "L'utilisateur a essayé de mettre à jour son email avec un email déjà existant",
    },
  },
  USER_EMAIL_UPDATE_LINK_NOT_SENDED: {
    code: "USER_EMAIL_UPDATE_LINK_NOT_SENDED",
    description: {
      en: "User try to update his/her email without sending an a validation link",
      fr: "L'utilisateur a essayé de mettre à jour son email sans envoyer de lien de validation",
    },
  },
  USER_EMAIL_UPDATE_TOKEN_ERROR: {
    code: "USER_EMAIL_UPDATE_TOKEN_ERROR",
    description: {
      en: "User try to update his/her email by token in validation link in wrong",
      fr: "L'utilisateur a essayé de mettre à jour son email avec un jeton de validation incorrect",
    },
  },
  USER_NEW_ACCESS_TOKEN: {
    code: "USER_NEW_ACCESS_TOKEN",
    description: {
      en: "User generate new access token",
      fr: "L'utilisateur a généré un nouveau jeton d'accès",
    },
  },
  USER_EMAIL_VALIDATION_SENDED: {
    code: "USER_EMAIL_VALIDATION_SENDED",
    description: {
      en: "User email validation sended",
      fr: "Validation d'email envoyée à l'utilisateur",
    },
  },
  USER_DELETED: {
    code: "USER_DELETED",
    description: {
      en: "The user is in deletion",
      fr: "L'utilisateur est en cours de suppression",
    },
  },
  USER_AVATAR_UPDATED: {
    code: "USER_AVATAR_UPDATED",
    description: {
      en: "User avatar updated",
      fr: "Avatar de l'utilisateur mis à jour",
    },
  },
  USER_DATA_UPDATED: {
    code: "USER_DATA_UPDATED",
    description: {
      en: "User data updated",
      fr: "Données de l'utilisateur mises à jour",
    },
  },
  USER_TYPE_UPDATED: {
    code: "USER_TYPE_UPDATED",
    description: {
      en: "User type updated",
      fr: "Type d'utilisateur mis à jour",
    },
  },
  USER_ROLE_UPDATED: {
    code: "USER_ROLE_UPDATED",
    description: {
      en: "User role updated",
      fr: "Rôle de l'utilisateur mis à jour",
    },
  },
  USER_PREFERENCES_UPDATED: {
    code: "USER_PREFERENCES_UPDATED",
    description: {
      en: "User preferences updated",
      fr: "Préférences de l'utilisateur mises à jour",
    },
  },
  USER_AVATAR_DELETED: {
    code: "USER_AVATAR_DELETED",
    description: {
      en: "User avatar updated",
      fr: "Avatar de l'utilisateur supprimé",
    },
  },
  USER_ADDED_TO_GROUP: {
    code: "USER_ADDED_TO_GROUP",
    description: {
      en: "User add to group",
      fr: "Utilisateur ajouté au groupe",
    },
  },
  USER_REMOVED_TO_GROUP: {
    code: "USER_REMOVED_TO_GROUP",
    description: {
      en: "User removed to group",
      fr: "Utilisateur retiré du groupe",
    },
  },
  USER_UPDATE_PASSWORD_TOKEN_ERROR: {
    code: "USER_UPDATE_PASSWORD_TOKEN_ERROR",
    description: {
      en: "User try to update his/her password with wrong password",
      fr: "L'utilisateur a essayé de mettre à jour son mot de passe avec un mot de passe incorrect",
    },
  },
  USER_REQUEST_GENERATE_RANDOM_PASSWORD: {
    code: "USER_REQUEST_GENERATE_RANDOM_PASSWORD",
    description: {
      en: "A random password is generated for the user",
      fr: "Un mot de passe aléatoire a été généré pour l'utilisateur",
    },
  },
  USER_EMAIL_VALIDATION_TOKEN_ERROR: {
    code: "USER_EMAIL_VALIDATION_TOKEN_ERROR",
    description: {
      en: "User email validation token error",
      fr: "Erreur de jeton de validation d'email de l'utilisateur",
    },
  },
  USER_LOGIN_FORM_VALIDATION_TOKEN_SUCCESS: {
    code: "USER_LOGIN_FORM_VALIDATION_TOKEN_SUCCESS",
    description: {
      en: "User token validation via login form success",
      fr: "Validation du jeton de l'utilisateur via le formulaire de connexion réussie",
    },
  },
  USER_LOGIN_FORM_VALIDATION_TOKEN_ERROR: {
    code: "USER_LOGIN_FORM_VALIDATION_TOKEN_ERROR",
    description: {
      en: "User token validation via login form failed",
      fr: "Échec de la validation du jeton de l'utilisateur via le formulaire de connexion",
    },
  },
  USER_LOGIN_BANNED: {
    code: "USER_LOGIN_BANNED",
    description: {
      en: "User try to login but his/her account is banned",
      fr: "L'utilisateur a essayé de se connecter mais son compte est banni",
    },
  },
  USER_ACCOUNT_STATUS_UPDATED: {
    code: "USER_ACCOUNT_STATUS_UPDATED",
    description: {
      en: "User account status updated",
      fr: "Statut du compte utilisateur mis à jour",
    },
  },
  USER_DATA_ADDED: {
    code: "USER_DATA_ADDED",
    description: {
      en: "User data added",
      fr: "Données utilisateur ajoutées",
    },
  },
  USER_DATA_REMOVED: {
    code: "USER_DATA_REMOVED",
    description: {
      en: "User data removed",
      fr: "Données utilisateur supprimées",
    },
  },
  USER_LOGOUT_SUCCESS: {
    code: "USER_LOGOUT_SUCCESS",
    description: {
      en: "User logged out successfully",
      fr: "Déconnexion utilisateur réussie",
    },
  },
  ENTITY_WEBHOOK_EVENT_CONFIRMED: {
    code: "ENTITY_WEBHOOK_EVENT_CONFIRMED",
    description: {
      en: "Webhook event confirmation received",
      fr: "Confirmation d'événement webhook reçue",
    },
  },
  ENTITY_USER_MESSAGE_SENT: {
    code: "ENTITY_USER_MESSAGE_SENT",
    description: {
      en: "An email was sent to the user",
      fr: "Un email a été envoyé à l'utilisateur",
    },
  },
  USER_PREFERENCES_REMOVED: {
    code: "USER_PREFERENCES_REMOVED",
    description: {
      en: "User preferences removed",
      fr: "Préférences utilisateur supprimées",
    },
  },
  USER_PREFERENCES_ADDED: {
    code: "USER_PREFERENCES_ADDED",
    description: {
      en: "User preferences added",
      fr: "Préférences utilisateur ajoutées",
    },
  },
  USER_BUSINESS_CARD_SCANNED: {
    code: "USER_BUSINESS_CARD_SCANNED",
    description: {
      en: "A business card was scanned for the user",
      fr: "Une carte de visite a été scannée pour l'utilisateur",
    },
  },
  ENTITY_SMS_MESSAGE_SENT: {
    code: "ENTITY_SMS_MESSAGE_SENT",
    description: {
      en: "An SMS was sent to the user",
      fr: "Un SMS a été envoyé à l'utilisateur",
    },
  },
  ENTITY_WHATSAPP_MESSAGE_SENT: {
    code: "ENTITY_WHATSAPP_MESSAGE_SENT",
    description: {
      en: "A WhatsApp message was sent to the user",
      fr: "Un message WhatsApp a été envoyé à l'utilisateur",
    },
  },
  ENTITY_WHATSAPP_MESSAGE_FAILED: {
    code: "ENTITY_WHATSAPP_MESSAGE_FAILED",
    description: {
      en: "A WhatsApp message could not be delivered, its credit was given back",
      fr: "Un message WhatsApp n'a pas pu être remis, son crédit a été rendu",
    },
  },
  WHATSAPP_OPT_IN: {
    code: "WHATSAPP_OPT_IN",
    description: {
      en: "The user accepted to receive WhatsApp messages",
      fr: "L'utilisateur a accepté de recevoir des messages WhatsApp",
    },
  },
  WHATSAPP_OPT_OUT: {
    code: "WHATSAPP_OPT_OUT",
    description: {
      en: "The user unsubscribed from WhatsApp messages",
      fr: "L'utilisateur s'est désinscrit des messages WhatsApp",
    },
  },
  LP_SMS_MESSAGE_SCHEDULED: {
    code: "LP_SMS_MESSAGE_SCHEDULED",
    description: {
      en: "The message has been scheduled to be sent via SMS",
      fr: "Le message a été programmé pour être envoyé par SMS",
    },
  },
  LP_USER_MESSAGE_SCHEDULED: {
    code: "LP_USER_MESSAGE_SCHEDULED",
    description: {
      en: "An email has been scheduled to be sent to the user",
      fr: "Un email a été programmé pour être envoyé à l'utilisateur",
    },
  },
  LP_USER_SCANNED: {
    code: "LP_USER_SCANNED",
    description: {
      en: "User QR code scanned by the Loyalty Programs application",
      fr: "Code QR de l'utilisateur scanné par l'application Loyalty Programs",
    },
  },
};

const UserTracker = ({
  isRoute = false,
  hideHeading = false,
  userIdentification,
}: {
  isRoute?: boolean;
  hideHeading?: boolean;
  userIdentification?: string;
}) => {
  const { t, i18n } = useTranslation();
  const { customer_identification, userEndedSubscription, user } = useAuth();
  const { user_identification: routeUserIdentification } = useParams();
  const user_identification = userIdentification ?? routeUserIdentification;

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    reset,
  } = useForm<{ events: string[] }>({
    resolver: joiResolver(schema),
  });

  const [show, setShow] = useState(false);
  const [trackerResult, setTrackerResult] = useState<string>("");
  const [trackerObjResult, setTrackerObjResult] = useState<any[]>([]);
  const [eventDetail, setEventDetail] = useState<Record<string, any>>();
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);

  moment.locale(momentLocaleOf(i18n.language));

  const { data, isSuccess, isError, isLoading, refetch } = useGetTrackerOptions(
    {
      customer_identification: customer_identification,
      entityIdentification: user.entity_identification,
    }
  );

  const {
    mutate,
    isSuccess: isSuccessUpdate,
    isLoading: isUpdating,
    data: updateData,
  } = useUpdateTracker();

  const handleCloseOptionsModal = () => {
    setShow(false);
  };

  const actorLabel = (actor: any): string | null => {
    if (!actor?.type) return null;
    if (actor.type === "OWNER") {
      return actor.impersonation
        ? t("tracker.actor-owner-impersonated")
        : t("tracker.actor-owner");
    }
    if (actor.type === "ROOT") return t("tracker.actor-root");
    if (actor.type === "PARTNER") return t("tracker.actor-partner");
    if (actor.type === "CONNECTOR") return t("tracker.actor-connector");
    return null;
  };

  const whatsappTemplateLabel = (value: any): string | null => {
    if (typeof value === "string") return value.trim() || null;
    if (!value || typeof value !== "object") return null;
    if (typeof value.name === "string" && value.name.trim()) {
      return value.name.trim();
    }
    if (typeof value.template === "string" && value.template.trim()) {
      return t(
        `dashboard.entities.viewEntityUser.sendWhatsApp.templates.${value.template}`,
        { defaultValue: value.template }
      ) as string;
    }
    return typeof value.content_sid === "string" ? value.content_sid : null;
  };

  const toExportableActor = (actor: any) =>
    actor
      ? {
          type: actor.type ?? null,
          impersonation: actor.impersonation === true,
          ...(actor.type === "OWNER"
            ? {
                user_identification: actor.user_identification ?? null,
                entity_identification: actor.entity_identification ?? null,
              }
            : {}),
        }
      : null;

  const toExportableEvent = (item: any) => ({
    date_time: item?.date_time ?? null,
    event: item?.event ?? null,
    message: item?.value?.message ?? null,
    details: item?.value?.value ?? null,
    actor: toExportableActor(item?.value?.actor),
  });

  const handleDownloadTrackerResult = (result: string) => {
    let dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(result);

    let linkRef: HTMLAnchorElement = document.createElement("a");
    linkRef.href = dataStr;
    linkRef.download = "eventList.json";

    linkRef.click();
  };

  const getTrackerData = async (events: string[]) => {
    await mutate(
      {
        customer_identification,
        entity_identification: user.entity_identification,
        user_identification,
        events: events,
      },
      {
        onSuccess: (data: AxiosResponse) => {
          setTrackerResult(
            JSON.stringify(
              (data?.data?.element ?? []).map(toExportableEvent),
              null,
              "\t"
            )
          );
          const sortedItems = data?.data?.element?.reduce(
            (acc: any[], item: any) => {
              const index = acc.findIndex(
                (el) =>
                  new Date(el.date_time).getTime() <
                  new Date(item.date_time).getTime()
              );
              if (index === -1) {
                acc.push(item);
              } else {
                acc.splice(index, 0, item);
              }
              return acc;
            },
            []
          );
          setTrackerObjResult([...sortedItems]);

          handleCloseOptionsModal();
        },
      }
    );
  };
  const onSubmit = (values: { events: string[] }) => {
    getTrackerData(values.events);
  };

  const handleShowEventDetails = (event: Record<string, any>) => {
    setEventDetail(event);
    setShowEventDetailsModal(true);
  };

  let optionsContent;

  if (isLoading) optionsContent = <p>Loading...</p>;

  if (isError)
    optionsContent = (
      <div>
        <h4>Failed to fetch options.</h4>
        <CustomButton color="light" onClick={() => refetch()} title="Retry" />
      </div>
    );

  if (isSuccess)
    optionsContent = (
      <div className="max-h-[320px] overflow-y-auto px-1 mb-4">
        {!!errors.events && (
          <small className="block mb-3 text-red-500">
            {errors.events.message}
          </small>
        )}
        {data
          .filter((x: string) => x != "USER_BUSINESS_CARD_SCANNED")
          .slice(0)
          .reverse()
          .map((item: string) => (
            <div className="flex items-center gap-2 mb-4" key={item}>
              <Checkbox
                id={`track-option-${item}`}
                value={item}
                {...register("events", {
                  onChange(event) {
                    if (event.target.value === "ALL") {
                      reset({ events: ["ALL"] });
                    } else {
                      reset({
                        events: getValues("events").filter(
                          (item) => item !== "ALL"
                        ),
                      });
                    }
                  },
                })}
              />
              <Label
                htmlFor={`track-option-${item}`}
                className="flex text-sm font-normal text-gray-700"
              >
                {eventList[item]?.description[getLanguageCode(i18n.language)]}
              </Label>
            </div>
          ))}
      </div>
    );

  const trackerMutationFn = useCallback(
    (events: string[]) => {
      getTrackerData(events);
    },
    [mutate]
  );

  useEffect(() => {
    if (isSuccess) {
      reset({
        events: [
          "USER_CREATED",
          "USER_LOGIN_SUCCESS",
          "ENTITY_USER_MESSAGE_SENT",
        ],
      });
      trackerMutationFn([
        "USER_CREATED",
        "USER_LOGIN_SUCCESS",
        "ENTITY_USER_MESSAGE_SENT",
      ]);
    }
  }, [isSuccess, reset, trackerMutationFn]);

  const embedded = !isRoute;

  return (
    <div className={embedded ? "flex h-full flex-col p-6" : "p-6"}>
      {isRoute && (
        <Link
          to={`/control-panel/users/${user_identification}`}
          className="gap-2 inline-flex items-center mb-4"
        >
          <div className="w-5 h-5 relative">
            <HiArrowLeft size={20} className="text-gray-400" />
          </div>
          <div className="grow shrink basis-0 text-gray-500 text-sm font-semibold font-figtree leading-tight">
            {t("dashboard.entities.viewEntityUser.key1_info")}
          </div>
        </Link>
      )}

      {!hideHeading && (
        <>
          <h2 className="text-gray-700 text-2xl font-bold font-figtree leading-none mb-2">
            {t("tracker.title")}
          </h2>
          <p className="text-gray-900 text-base font-normal font-figtree leading-normal">
            {t("tracker.description")}
          </p>
        </>
      )}

      <CustomButton
        className={`${
          embedded ? "w-full shrink-0" : "w-[300px]"
        } justify-between flex-row-reverse`}
        onClick={() => setShow(true)}
        disabled={userEndedSubscription}
        title={
          getValues("events")?.indexOf("ALL") <= -1
            ? t("tracker.btn-1")
            : t("tracker.btn-2")
        }
        icon="chevronDown"
      />

      {isSuccessUpdate && (
        <div
          className={embedded ? "mt-3 flex min-h-0 flex-1 flex-col" : "my-3"}
        >
          <div
            className={
              embedded
                ? "min-h-0 flex-1 overflow-y-auto rounded-md border border-gray-200 p-3"
                : "rounded-md border border-gray-200 p-3"
            }
            style={embedded ? undefined : { maxHeight: 300, overflowY: "auto" }}
          >
            {trackerObjResult.length <= 0 ? (
              <div className="py-4 text-center">
                <h4 className="text-sm text-gray-500">{t("tracker.text-1")}</h4>
              </div>
            ) : (
              <ol className="list-none">
                {trackerObjResult.map((item: any, index: number) => (
                  <li
                    key={index}
                    className="mb-4 flex flex-row justify-between"
                  >
                    <div className="content">
                      <h3 className="text-gray-900 text-base font-medium font-figtree leading-normal">
                        {
                          eventList[item?.event]?.description[
                            getLanguageCode(i18n.language)
                          ]
                        }
                      </h3>
                      <p className="text-gray-500 text-xs font-normal font-figtree leading-none">
                        {moment.utc(item?.date_time).local().format("LLLL")}
                      </p>
                      {item?.event === "ENTITY_WHATSAPP_MESSAGE_SENT" &&
                        whatsappTemplateLabel(item?.value?.value) && (
                          <p className="mt-1 text-xs font-normal font-figtree leading-none text-gray-500">
                            {whatsappTemplateLabel(item?.value?.value)}
                          </p>
                        )}
                      {actorLabel(item?.value?.actor) && (
                        <p className="mt-1 text-xs font-normal font-figtree leading-none text-gray-400">
                          {actorLabel(item?.value?.actor)}
                        </p>
                      )}
                    </div>

                    {item?.value?.value && (
                      <CustomButton
                        variant="none"
                        className="border-none ring-0 outline-none"
                        onClick={() => handleShowEventDetails(item)}
                        icon="documentSearch"
                      />
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>

          <CustomButton
            onClick={() => handleDownloadTrackerResult(trackerResult)}
            disabled={!updateData.data || updateData.data.length <= 0}
            className={embedded ? "mt-4 w-full shrink-0" : "mt-6 w-full"}
            variant="primary"
            icon={<HiOutlineDownload size={20} />}
            title={t("tracker.btn-download")}
          />
        </div>
      )}

      <ModalShell
        show={show}
        onClose={handleCloseOptionsModal}
        size={"md"}
        title={t("tracker.title")}
        subtitle={t("tracker.description-modal")}
        onSubmit={handleSubmit(onSubmit)}
        footer={
          <CustomButton
            variant="primary"
            type="submit"
            disabled={!isValid || isUpdating}
            isLoading={isUpdating}
            title={t("tracker.btn-submit-modal")}
          />
        }
      >
        <div className="options">{optionsContent}</div>
      </ModalShell>

      <ModalShell
        show={showEventDetailsModal}
        onClose={() => {
          setEventDetail(undefined);
          setShowEventDetailsModal(false);
        }}
        dismissible
        title={
          eventList[eventDetail?.event]?.description[
            getLanguageCode(i18n.language)
          ]
        }
        subtitle={
          <>
            {moment.utc(eventDetail?.date_time).local().format("LLLL")}
            {actorLabel(eventDetail?.value?.actor) && (
              <span className="mt-0.5 block text-xs text-gray-400">
                {actorLabel(eventDetail?.value?.actor)}
              </span>
            )}
          </>
        }
      >
        {eventDetail?.event === "ENTITY_WHATSAPP_MESSAGE_SENT" ? (
          <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 rounded-md border border-gray-300 p-4">
            <dt className="text-xs text-gray-500">
              {t("tracker.wa-template")}
            </dt>
            <dd className="text-sm text-gray-800">
              {whatsappTemplateLabel(eventDetail?.value?.value) ?? "—"}
            </dd>
            {typeof eventDetail?.value?.value === "object" &&
              eventDetail?.value?.value?.content_sid && (
                <>
                  <dt className="text-xs text-gray-500">
                    {t("tracker.wa-reference")}
                  </dt>
                  <dd className="break-all text-xs text-gray-400">
                    {eventDetail.value.value.content_sid}
                  </dd>
                </>
              )}
          </dl>
        ) : (
          <div className="whitespace-pre-wrap break-words rounded-md border border-gray-300 p-4 text-sm leading-tight text-gray-600">
            {typeof eventDetail?.value?.value === "string" &&
            eventDetail.value.value.includes("<")
              ? parseSafeHtml(eventDetail.value.value)
              : typeof eventDetail?.value?.value === "string"
              ? eventDetail.value.value
              : JSON.stringify(eventDetail?.value?.value, null, 2)}
          </div>
        )}
      </ModalShell>
    </div>
  );
};

export default UserTracker;
